export const technologies = [
    // ── Message Brokers & Streaming ──────────────────────────────
    {
        id: 'apache-kafka',
        category: 'Message Brokers & Streaming',
        title: 'Apache Kafka',
        summary: 'Distributed event streaming platform used for high-performance data pipelines, streaming analytics, and data integration.',
        content: `
## Apache Kafka Deep Dive

Apache Kafka is radically different from traditional message queues (like RabbitMQ). Instead of a temporary holding pen where messages are deleted after reading, Kafka acts as a massive, durable, append-only **Distributed Commit Log**.

### Core Architecture

1. **Topics:** The logical categories to which events are published (like a database table). A topic is an append-only log.
2. **Partitions:** To scale beyond a single server, a topic is broken into partitions spread across multiple broker nodes. This allows Kafka to horizontally scale both storage and parallel processing. Order is strictly guaranteed *only within a partition*.
3. **Producers:** Applications that publish events to a Kafka topic. They choose which partition to write to (often by hashing a key, like \\\`user_id\\\`).
4. **Consumers & Consumer Groups:** Applications that read events. A Consumer Group allows you to parallelize reading: if a topic has 10 partitions and your group has 10 consumers, each consumer reads exactly 1 partition.
5. **Brokers:** The physical servers that store the data.
6. **ZooKeeper (or KRaft):** The consensus controller that manages cluster metadata and leader election among brokers.

<diagram>
graph TD
    subgraph Producers
    P1[Payment API]
    P2[Click Tracker]
    end

    subgraph Kafka Cluster
    direction LR
    B1[Broker 1] --- ZK[(ZooKeeper / KRaft)]
    B2[Broker 2] --- ZK
    Topic[Topic: user-events]
    B1 --- Topic
    B2 --- Topic
    end

    subgraph Consumer Group A
    C1[Worker 1] --> |Reads P0| Topic
    C2[Worker 2] --> |Reads P1| Topic
    end

    P1 -->|Appends to Log| Topic
    P2 -->|Appends to Log| Topic
</diagram>

### Key Characteristics & Tradeoffs

- **Massive Throughput:** Kafka heavily relies on the OS page cache and Sequential Disk I/O. Writing to a sequential log on an HDD is actually faster than random writes on an SSD. It can handle millions of messages per second.
- **Durability & Replayability:** Messages are retained for a configurable time (e.g., 7 days) and replicated across brokers. If a consumer crashes, it can boot up and "replay" history from an offset.
- **Tradeoff:** Kafka is highly complex to manage operationally. It is not designed for complex per-message routing or delayed scheduling (use RabbitMQ for that).

### System Design Use Cases
- **Activity Tracking:** Tracking user clicks and telemetry on a massive scale.
- **Event Sourcing:** Serving as the central nervous system for microservices, allowing them to react to a stream of domain events asynchronously.
- **Log Aggregation:** Siphoning logs from thousands of servers into a centralized analytics engine (like Elasticsearch).
  `
    },
    {
        id: 'rabbitmq',
        category: 'Message Brokers & Streaming',
        title: 'RabbitMQ',
        summary: 'A robust, mature point-to-point message broker supporting complex routing topologies over AMQP.',
        content: `
## RabbitMQ

While Kafka is an append-only distributed log used for massive event streaming, **RabbitMQ** is the industry standard for traditional, heavy-duty "Message Queuing."

### Smart Broker, Dumb Consumer
- **Kafka:** The broker just stores logs dumbly. The *Consumers* are smart — they track their own offsets and pull data.
- **RabbitMQ:** The broker is highly intelligent. It pushes messages to consumers, tracks exactly who acknowledged what, and handles complex routing logic.

### Exchanges and Routing Keys
When a Producer sends a message, it does not send it directly to a queue. It sends it to an **Exchange**. The Exchange uses binding rules to route the message to the correct queue(s).

1. **Direct Exchange:** Routes messages precisely via a routing key (e.g., "pdf.generate").
2. **Topic Exchange:** Routes messages based on wildcard patterns (e.g., "logs.error.*").
3. **Fanout Exchange:** Ignores routing keys and blindly broadcasts the message to every queue bound to it.

### Why choose RabbitMQ over Kafka?
- **Complex Routing:** If you need wildcards and intricate rules for where messages go.
- **Per-Message ACKs:** In RabbitMQ, a worker can process message 1, fail on message 2, and process message 3. The broker will re-queue message 2 automatically. Kafka forces sequential processing within a partition.
- **Delayed/Scheduled Messages:** RabbitMQ natively supports putting a message in a queue but hiding it for exactly 15 minutes (great for "Send reminder email in 15 mins"). Kafka cannot do this natively.

<diagram>
graph LR
    P[Producer] --> |Routing Key: image.resize| X{Exchange: Direct}

    X -->|Matches image.resize| Q1[(Queue: Image Workers)]
    X -->|Matches video.encode| Q2[(Queue: Video Workers)]

    Q1 --> W1[Worker 1]
    Q1 --> W2[Worker 2]
</diagram>
  `
    },

    // ── Caching & In-Memory ──────────────────────────────────────
    {
        id: 'redis',
        category: 'Caching & In-Memory',
        title: 'Redis',
        summary: 'Open-source, in-memory data structure store, used as a database, cache, and message broker.',
        content: `
## Redis: More Than Just a Cache

Redis (Remote Dictionary Server) is famous for being a hyper-fast, in-memory caching system. However, its true power lies in the fact that it is an advanced **Data Structures Server**.

### Why is Redis so fast?

1. **Memory-First:** It stores all datasets in RAM. There is zero disk I/O bottleneck during a read/write operation. Response times are consistently sub-millisecond.
2. **Single-Threaded Event Loop:** Redis commands execute on a single core. This eliminates absolutely all context-switching overhead and thread-locking contention. Operations are inherently atomic.
3. **Optimized C Code:** Data structures are engineered for maximum efficiency.

### Core Data Structures & Interview Tactics

When asked how to solve a specific speed problem in an interview, knowing which Redis data structure to reach for is the silver bullet.

- **Strings:** Simple key-value. *Use case:* Caching HTML pages, JSON API responses, or simple atomic counters (INCR page_views).
- **Lists:** Linked lists. *Use case:* A lightweight, basic message queue or a timeline feed (e.g., LPUSH to a user's feed, trim to only keep the latest 100 items).
- **Sets:** Unordered collection of unique strings. *Use case:* Finding mutual friends (SINTER), tracking unique IPs visiting a site.
- **Sorted Sets (ZSET):** Sets ordered by a floating-point "score". *Use case:* **Real-time Leaderboards** (ranking gaming players by score), Sliding Window Rate Limiters (using timestamps as scores).
- **Hashes:** Like a JSON object (field-value pairs). *Use case:* Storing user profiles compactly.

<diagram>
sequenceDiagram
    participant Client
    participant AppServer
    participant Redis as Redis (Primary)
    participant Replica as Redis (Replica)
    participant Disk as AOF/RDB Disk

    Client->>AppServer: Get Leaderboard
    AppServer->>Redis: ZREVRANGE global_scores 0 10
    Redis-->>AppServer: Top 10 Players (0.5ms)
    AppServer-->>Client: Display UI

    AppServer->>Redis: ZINCRBY global_scores 50 UserA
    Redis->>Replica: Async Replication sync
    Redis->>Disk: Async fsync (AOF log)
    Redis-->>AppServer: OK
</diagram>

### Durability (Disaster Recovery)
If Redis is in RAM, what happens when you pull the plug? Redis offers two persistence models:
1. **RDB (Snapshots):** Dumps the entire RAM state to a binary disk file every X minutes. Fast, but you lose data since the last snapshot on crash.
2. **AOF (Append Only File):** Logs every single write command to disk in the background. Slower I/O, but very little data loss.

*In interviews, use Redis Cluster for horizontal scaling (sharding data across multiple Redis nodes) and Sentinel for high-availability auto-failover.*
  `
    },
    {
        id: 'memcached',
        category: 'Caching & In-Memory',
        title: 'Memcached',
        summary: 'A high-performance, distributed memory object caching system intended to speed up dynamic web apps by alleviating DB load.',
        content: `
## Memcached vs Redis

Before Redis dominated the ecosystem, **Memcached** was the absolute king of caching. It is still heavily used today in massive legacy systems (like early Facebook and Twitter architectures).

### The Core Difference
While Redis is a "Data Structure Server" (supporting Lists, Sets, Hashes, Geospatial indexes), **Memcached is strictly a simple Key-Value store**. You put a string in, you pull a string out.

### Advantages of Memcached
1. **Multi-Threaded Architecture:** Redis is famously single-threaded. It processes commands exactly one by one. Memcached is multi-threaded. If you have a massive 64-core server, Memcached will utilize every single core to serve concurrent GET requests faster than Redis can on a single machine.
2. **Simpler Memory Management:** Because it only stores raw strings and does not manage complex data structures, its memory allocator is phenomenally efficient and less prone to fragmentation.

### Disadvantages (Why Redis won)
Memcached has **Zero Disk Persistence**. If the server restarts, every single byte of cached data evaporates instantly (a catastrophic "cold cache" scenario). It also lacks replication out-of-the-box.

*Interview Rule of Thumb:* Always suggest Redis by default for its versatility, persistence, and data structures. Only mention Memcached if specifically asked about multi-threaded CPU utilization for pure, simple, read-only caching.
  `
    },

    // ── NoSQL Databases ──────────────────────────────────────────
    {
        id: 'cassandra',
        category: 'NoSQL Databases',
        title: 'Apache Cassandra',
        summary: 'A highly scalable, distributed wide-column NoSQL database designed for extreme availability and write-heavy workloads.',
        content: `
## Apache Cassandra

Cassandra is a distributed wide-column NoSQL database heavily inspired by Amazon's Dynamo (for its global distribution model) and Google's Bigtable (for its storage engine). It was built to solve the ultimate system design problem: **How do we handle massive, continuous writes across global data centers with zero single points of failure?**

### The Masterless Ring Architecture

Unlike traditional databases that use a Master-Slave setup, Cassandra is entirely decentralized (Peer-to-Peer).
- Every physical node in the cluster is identical. There is no "Master" node that can become a bottleneck.
- Nodes are arranged in a **logical ring**.
- Data is distributed across these nodes using a **Consistent Hashing** algorithm based on the table's Partition Key.

<diagram>
graph TD
    Client[API Server]

    subgraph Cassandra Cluster Ring
    Node1((Node 1 Token 0-25))
    Node2((Node 2 Token 26-50))
    Node3((Node 3 Token 51-75))
    Node4((Node 4 Token 76-100))

    Node1 --- Node2
    Node2 --- Node3
    Node3 --- Node4
    Node4 --- Node1
    end

    Client -.->|Write Hash=30| Node2
    Client -.->|Replica 1| Node3
    Client -.->|Replica 2| Node4
</diagram>

### Tunable Consistency (AP under CAP)

Cassandra is deeply rooted in the **AP (Availability & Partition Tolerance)** side of the CAP theorem. However, it gives developers "Tunable Consistency" per query.

Data is automatically replicated across multiple nodes (defined by the Replication Factor). When you read or write, you specify a **Consistency Level (CL)**:
- **CL = ONE:** Lightning fast. The query succeeds as soon as 1 node responds. Highly available, but the data might be stale.
- **CL = QUORUM:** The majority of nodes (e.g., 2 out of 3 replicas) must respond. This provides strong consistency while surviving minority node failures.
- **CL = ALL:** Perfect consistency. But if even one replica node goes offline, the query fails (terrible availability).

### The Data Model (Wide-Column)
Data is modeled explicitly around your read queries, not tabular normalization.
- **Partition Key:** Determines which physical node the data lives on (e.g., user_id).
- **Clustering Key:** Determines how the data is sorted on disk within that partition (e.g., timestamp DESC).
- **Result:** You can fetch millions of chronologically ordered messages for a user with a single O(1) disk sweep.

### Interview Use Cases
- **Time-Series Data:** IoT sensor data, stock market ticks, application metrics.
- **Heavy Write Loads:** Activity logging, chat message history (Discord migrated billions of messages successfully to Cassandra).
  `
    },
    {
        id: 'mongodb',
        category: 'NoSQL Databases',
        title: 'MongoDB',
        summary: 'A document-oriented NoSQL database that stores data in flexible, JSON-like BSON format, prioritizing query flexibility and developer speed.',
        content: `
## MongoDB (Document Store)

MongoDB is the undisputed king of Document databases. Instead of rigid rows and columns (SQL), or wide-column families (Cassandra), MongoDB stores data in flexible, JSON-like documents.

### The Document Paradigm

In a relational database, storing a User, their Address, and their multiple Phone Numbers requires three separate tables, strict foreign keys, and slow JOIN operations.

In MongoDB, data that is accessed together is stored together in a single hierarchical document. A user document can embed their address and phone array directly, eliminating JOINs entirely.

- **Pros:** It eliminates the need for complex, CPU-heavy JOINs. Reading a user profile is a single, extremely fast disk lookup. The schema is entirely dynamic — you can add a new "twitter_handle" field to one document without restructuring a massive table.
- **Cons:** Data duplication (Denormalization). If the city name "Delhi" changes officially, you must update thousands of isolated documents instead of one central "Cities" table row.

### Architecture: Replica Sets & Sharding

MongoDB is a **CP system** (Consistent and Partition Tolerant) under the CAP theorem. It uses a Master-Slave architecture.

1. **Replica Sets (High Availability):** A primary node handles all writes. Secondary nodes replicate the data asynchronously. If the primary crashes, the secondaries hold an automatic election to promote a new primary.
2. **Sharding (Horizontal Scaling):** When data exceeds a single server's disk space, MongoDB uses a query router (mongos) to split the documents across multiple shards (replica sets) based on a clever Shard Key.

<diagram>
graph TD
    Client[Client App] --> Router[Mongos Query Router]

    subgraph Shard 1 A-M users
        P1[Primary Node] --- S1[Secondary]
        P1 --- S2[Secondary]
    end

    subgraph Shard 2 N-Z users
        P2[Primary Node] --- S3[Secondary]
        P2 --- S4[Secondary]
    end

    Router -->|If key=Adam| P1
    Router -->|If key=Zack| P2
</diagram>

### Interview Use Cases
- **Content Management Systems (CMS):** Storing blog posts with dynamic metadata, tags, and nested comments.
- **E-Commerce Catalogs:** Products have wildly varying attributes. A TV has "resolution", while a Shirt has "fabric" and "size". A schema-less document handles this beautifully without requiring hundreds of NULL columns.
  `
    },
    {
        id: 'dynamodb',
        category: 'NoSQL Databases',
        title: 'Amazon DynamoDB',
        summary: 'A fully managed, serverless, key-value NoSQL database designed to run high-performance applications at any scale.',
        content: `
## Amazon DynamoDB

DynamoDB is AWS's flagship NoSQL database. Unlike deploying Cassandra or MongoDB where you must provision and manage the underlying EC2 servers, DynamoDB is completely **Serverless**. You simply create a table, and AWS handles the infinite horizontal scaling automatically.

### Architecture & Partitioning
- **Keys:** Every item must have a Primary Key. This can be a simple Partition Key (e.g., user_id) or a Composite Key (Partition Key + Sort Key, e.g., user_id + timestamp).
- **Under the Hood:** DynamoDB spreads your data across thousands of internal AWS servers based on the hash of your Partition Key. If you configure the table for 100,000 WCU (Write Capacity Units), AWS automatically provisions enough hidden shards to handle that throughput instantly.

### Why use DynamoDB in an Interview?

1. **Zero Operational Overhead:** If the prompt explicitly asks for "minimal ops" or a "small team," DynamoDB is the trump card over Cassandra.
2. **Single-Digit Millisecond Latency:** It guarantees sub-10ms latency for reads/writes at virtually infinite scale.
3. **DynamoDB Streams:** A built-in feature that captures a time-ordered sequence of item-level modifications. You can use this to trigger AWS Lambda functions asynchronously when a row changes (e.g., calculating a total score when a new game match result is inserted).

### The Tradeoffs (Very Important)
- **No Complex Queries:** You absolutely cannot do ad-hoc queries like "find users where age > 25" unless age is a Sort Key or a Secondary Index. If you try to scan a massive table without an index, it will cost thousands of dollars and time out.
- **Vendor Lock-in:** It only exists on AWS.

<diagram>
graph LR
    API[Application / Lambda] -->|Read/Write Hash: User123| Router[DynamoDB Request Router]

    subgraph AWS Managed Storage Nodes
        Router --> Node1[(Partition A)]
        Router --> Node2[(Partition B)]
        Router --> Node3[(Partition C)]
    end

    Node1 -.-> |DynamoDB Stream| Lambda[AWS Lambda: Send Welcome Email]
</diagram>
  `
    },

    // ── SQL Databases ────────────────────────────────────────────
    {
        id: 'postgresql',
        category: 'SQL Databases',
        title: 'PostgreSQL',
        summary: "The world's most advanced open-source relational database, known for strict ACID compliance, MVCC, and massive extensibility.",
        content: `
## PostgreSQL

When system design requires absolute data integrity, complex relational queries, and ACID compliance, PostgreSQL (Postgres) is the industry gold standard.

### Core Philosophy: ACID and Relational Integrity

Postgres is a traditional SQL database. It relies on strict schemas, normalization, foreign keys, and JOIN operations.
- **Atomicity, Consistency, Isolation, Durability (ACID):** It guarantees that financial transactions will either process completely, or safely roll back. There is no "eventual consistency" guessing game.
- **MVCC (Multi-Version Concurrency Control):** This is Postgres's secret weapon for performance. In older databases, if User A is reading a row while User B is writing to it, the database locks the row, forcing A to wait. MVCC keeps snapshot versions of the row. "Readers don't block writers, and writers don't block readers."

### Advanced Features (Why it beats MySQL in interviews)

1. **JSONB Support:** Postgres can actually act like a NoSQL document store. You can store JSON objects in a JSONB column, index them, and query deeply nested keys just like MongoDB, giving you the best of both SQL and NoSQL worlds.
2. **PostGIS:** An extension that turns Postgres into a spatial database, allowing for incredibly fast geographic queries (e.g., "Find all users within a 5-mile radius of these GPS coordinates").
3. **Full-Text Search:** Native, built-in search functionality that often delays the need to spin up a heavy Elasticsearch cluster.

<diagram>
graph TD
    subgraph Master-Slave Replication
    Master[(Primary DB Read/Write)]
    Master -- WAL Streaming --> Slave1[(Read Replica 1)]
    Master -- WAL Streaming --> Slave2[(Read Replica 2)]
    end

    API[Application Backend] -->|Write Operations| Master
    API -->|Read Operations| Slave1
    API -->|Read Operations| Slave2
</diagram>

### Scaling Relational Databases

Scaling Postgres horizontally is notoriously difficult. Instead, we use specific tactical patterns:
1. **Vertical Scaling First:** Max out the CPU, RAM, and PCIe NVMe drives on a single massive machine. Postgres is incredibly efficient at using vertical resources.
2. **Read Replicas:** The overwhelming majority of traffic is Reads. You stream the Write-Ahead Log (WAL) from the Primary DB to 5 Read-Only replicas, distributing the read load via a load balancer (like pgBouncer).
3. **Sharding (Last Resort):** Splitting tables across servers using tools like Citus Data.

### Interview Use Cases
- **Financial Systems & Billing:** Absolutely vital for payment processing ledgers where money cannot magically disappear.
- **Uber/Lyft Geo-Matching (early scaling):** Using PostGIS to match riders and drivers based on GPS grids.
- **Inventory Management:** Strictly enforcing that two people cannot buy the last ticket to a concert at the exact same millisecond.
  `
    },

    // ── Search & Analytics ───────────────────────────────────────
    {
        id: 'elasticsearch',
        category: 'Search & Analytics',
        title: 'Elasticsearch',
        summary: 'A distributed, RESTful search and analytics engine built on Apache Lucene, capable of full-text search at petabyte scale.',
        content: `
## Elasticsearch

Standard databases (SQL and NoSQL alike) are absolutely terrible at searching. If you run a query like "SELECT * FROM users WHERE bio LIKE '%developer%'", the database must perform a catastrophic "Full Table Scan", reading every single row on disk.

Elasticsearch was built to solve this. It is a distributed search engine built on top of the Apache Lucene library.

### The Secret: The Inverted Index

While a standard database creates a "Forward Index" (mapping Document ID 1 -> "The quick brown fox"), Elasticsearch creates an **Inverted Index** (mapping the words to the documents).

It tokenizes all text upon ingestion and creates a dictionary:
- "quick" -> Document 1, 3
- "brown" -> Document 1, 2
- "fox" -> Document 1, 2, 4

When a user searches for "brown fox", Elasticsearch achieves an O(1) hash-map lookup to instantly intersect the document lists, returning results in milliseconds out of massive datasets.

<diagram>
graph TD
    Client[Web Frontend] --> API[Backend API]
    API --> |1. Normal CRUD Write| DB[(Primary Database PostgreSQL)]
    DB -- 2. Change Data Capture / Kafka --> ES[Elasticsearch Cluster]

    API --> |3. User types in Search Bar| ES
    ES --> |4. Sub-50ms Search Results| API

    subgraph Inverted Index Concept
    Tokens[Tokens: apple to Doc1 Doc5 and macbook to Doc1]
    end
</diagram>

### Core Architecture
- **Documents & Indices:** Data is stored as JSON Documents. An Index is a logical collection of documents (analogous to a Database).
- **Sharding for Scale:** An index containing billions of documents is chopped into smaller "Shards" distributed across multiple physical nodes.
- **Replicas:** Every primary shard has replica shards on different nodes to ensure High Availability and increase read throughput.

### The ELK Stack (Log Aggregation)
Elasticsearch is the 'E' in the famous **ELK Stack** (Elasticsearch, Logstash, Kibana).
In microservices, you have 50 servers generating log files. Attempting to SSH into 50 servers to grep an error is impossible. Logstash ingests all logs, ships them to Elasticsearch, and Kibana provides a visual dashboard to instantly query and monitor application health across the entire fleet.

### Interview Use Cases
- **E-Commerce Search Bars:** Implementing fuzzy matching, autocomplete, and relevance scoring (TF-IDF/BM25) for Amazon-style product catalogs.
- **System Observability:** Centralizing logs and metrics from global microservices into one searchable pane of glass.
  `
    },

    // ── Graph Databases ──────────────────────────────────────────
    {
        id: 'neo4j',
        category: 'Graph Databases',
        title: 'Neo4j',
        summary: 'A native graph database built to handle highly connected data and complex relationship queries.',
        content: `
## Neo4j

Standard Relational (SQL) and NoSQL databases struggle immensely with highly interconnected data. If you want to find "Friends of Friends who like Pizza," a SQL database requires a massive, computationally catastrophic 4-way JOIN across a millions-row relationship table.

Neo4j is purpose-built to solve this. It is a **Graph Database**.

### Nodes and Edges
Instead of Tables and Rows, Neo4j uses the mathematical concept of a Graph.
- **Nodes:** The entities (e.g., User, City, Restaurant).
- **Edges (Relationships):** The lines connecting nodes. Edges have strict directions and can contain their own properties (e.g., KNOWS with a "since: 2010" property).

### Index-Free Adjacency
This is Neo4j's secret sauce for performance. In a relational database, traversing a relationship requires an expensive index lookup (B-Tree search) in the bridging table.
In Neo4j, every node physically contains direct memory pointers (like a highly advanced Linked List) to its adjacent nodes. Traversing a relationship is an instantaneous O(1) memory hop, rather than a O(log N) index search. Finding a "Friend of a Friend of a Friend" takes milliseconds, regardless of if the graph has 10 nodes or 10 billion nodes.

### Interview Use Cases
- **Social Networks:** LinkedIn's "3rd degree connection" calculation.
- **Recommendation Engines:** "Users who bought this item also bought..."
- **Fraud Detection:** Identifying rings of synthetic identities sharing the same phone number or credit card across 100 supposedly unrelated accounts.

<diagram>
graph TD
    UserA((User: Alice)) -- FRIENDS_WITH --> UserB((User: Bob))
    UserB -- FRIENDS_WITH --> UserC((User: Charlie))

    Pizza((Restaurant: Pizza Place))

    UserC -- LIKES rating 5 --> Pizza
    UserB -- LIKES --> Pizza
</diagram>
  `
    },

    // ── Coordination & Consensus ─────────────────────────────────
    {
        id: 'zookeeper',
        category: 'Coordination & Consensus',
        title: 'ZooKeeper / etcd',
        summary: 'Centralized services for maintaining configuration, naming, and providing distributed synchronization and group services.',
        content: `
## Distributed Coordination (ZooKeeper & etcd)

In a distributed system with hundreds of microservices, database shards, and message broker nodes, how do they all agree on fundamental truths? How do you prevent two services from acting as the "Primary Leader" simultaneously (the Split-Brain problem)?

You need a highly-available, strictly consistent **Coordination Service**. Apache ZooKeeper and etcd (the brains behind Kubernetes) fill this role.

### The Problem of Consensus
Network partitions make consensus incredibly difficult. If Node A loses connectivity to Node B, both might assume the other is dead and legally declare themselves the Primary Leader. This destroys data integrity.

Coordination services solve this using complex consensus algorithms (like **Paxos** or **Raft**).
- To make a decision, a **Quorum (majority)** of nodes in the ZooKeeper ensemble must agree.
- Because a quorum (e.g., 3 out of 5 nodes) is mathematically impossible to achieve in two isolated brain-halves, Split-Brain is entirely prevented.

<diagram>
graph TD
    subgraph ZooKeeper Ensemble
    Z1[ZK Follower]
    Z2[ZK Leader]
    Z3[ZK Follower]
    Z1 -.-> Z2
    Z3 -.-> Z2
    end

    subgraph Kafka Cluster Clients of ZK
    K1[Kafka Broker 1]
    K2[Kafka Broker 2]
    K3[Kafka Broker 3]
    end

    K1 -->|Heartbeats and Metadata| Z2
    K2 -->|Who is the topic leader?| Z2
    K3 -->|Register connection| Z2
</diagram>

### Key Architectures Powered by Coordination

1. **Leader Election:** If you have 5 background workers pulling tasks, you might want only 1 to legitimately assign the jobs. They all race to create an Ephemeral node in ZooKeeper. The one who wins becomes the Leader. If the Leader crashes, its Ephemeral node evaporates immediately due to a missed heartbeat, and a new election is triggered.
2. **Cluster Management (Service Discovery):** How do API Gateways know what IP address "User Service Node 4" is currently running on? Services register their IP in etcd upon boot, and de-register upon crash.
3. **Distributed Locking:** Safely acquiring a global mutex lock across geographically separated microservices to prevent race conditions on critical shared resources.
4. **Configuration Management:** Storing the global database_url dynamically. If updated in etcd, all 50 services are instantly notified of the change via watch events.

*Interview Tip: Treat ZooKeeper/etcd as the "source of absolute truth" in architectures that demand rigid consistency mechanisms.*
  `
    },

    // ── Object Storage ───────────────────────────────────────────
    {
        id: 's3-object-storage',
        category: 'Object Storage',
        title: 'Amazon S3',
        summary: 'Highly scalable, highly durable object storage service designed to store and retrieve any amount of unstructured data from anywhere.',
        content: `
## Amazon S3 (Object Storage)

When designing systems like YouTube, Instagram, or Netflix, you have to store Petabytes of unstructured video, audio, and image files. Traditional Relational Databases and Block Storage volumes (Hard Drives attached to EC2 instances) are completely incapable of scaling to this extreme.

You must utilize **Object Storage**, popularized globally by Amazon S3.

### Object Storage vs Block Storage

1. **Block Storage (EBS, local SSDs):** Acts like a traditional hard drive. Data is chunked into logical blocks. Excellent for low-latency database disks or OS boot drives, but you cannot easily share a hard drive across 10,000 servers.
2. **Object Storage (S3):** Data (the payload) is stored flat, alongside massive custom metadata and a unique identifier (URL). There are no infinite filesystem hierarchy trees to traverse.

### Core Traits of S3
- **Infinite Scale:** You can legally dump Exabytes of data into an S3 bucket without ever provisioning a server or resizing a volume.
- **Incredible Durability:** AWS guarantees "Eleven Nines" (99.999999999%) durability. They achieve this by transparently slicing and replicating your file across multiple completely isolated physical data centers (Availability Zones). It is practically impossible to lose a file.
- **Eventual Consistency (Historically):** Because data is wildly distributed, taking time to replicate, writing a file and immediately doing a global listing used to sometimes miss the file temporarily. (Note: AWS updated S3 to Strong Read-After-Write consistency recently, but the distributed theory remains crucial).

<diagram>
graph TD
    Client[Mobile App User] --> |1. Request Upload URL| API[Backend API]
    API --> |2. Generate signed URL| Client
    Client -- 3. Direct HTTP PUT bypassing API --> S3[(Amazon S3 Bucket)]
    S3 -- 4. Trigger Event Notification --> SQS[SQS Queue]
    SQS --> Worker[Video Transcoding Worker]
</diagram>

### System Design Tactics

1. **The Pre-Signed URL Pattern:** The most common interview trick for uploading media. You **never** stream a 2GB video through your Node.js API server to S3 (it will instantly crash your server due to bandwidth and memory starvation).
   - Instead, the API returns a temporary, cryptographically "Pre-Signed URL".
   - The Mobile App uses this URL to upload the video bytes *directly* to S3, totally bypassing your application servers.
2. **CDN Integration:** S3 is the standard "Origin Server" for Content Delivery Networks (like CloudFront). You dump static React bundles and images in S3, and CloudFront caches them globally at the edges.
3. **Data Lakes:** You dump massive, raw, untransformed JSON or Parquet logs into S3 for big-data tools (like Spark or AWS Athena) to query directly via SQL over HTTP.
  `
    },

    // ── Proxy & Edge Networking ──────────────────────────────────
    {
        id: 'nginx-haproxy',
        category: 'Proxy & Edge Networking',
        title: 'Nginx & HAProxy',
        summary: 'High-performance reverse proxies and load balancers for HTTP routing, SSL termination, and static asset delivery.',
        content: `
## Nginx & HAProxy (Reverse Proxies)

While Cloud providers offer managed Load Balancers (like AWS ALB or NLB), open-source titans like **Nginx** and **HAProxy** remain the industry standard for routing traffic, load balancing, and edge security in self-hosted or Kubernetes environments (like the Nginx Ingress Controller).

### Forward Proxy vs. Reverse Proxy

- **Forward Proxy:** Sits in front of the *Client*. (Example: A corporate firewall proxy. When you try to go to Facebook, the proxy intercepts your request, checks if Facebook is blocked, and if not, goes to Facebook on your behalf. The internet only sees the proxy's IP).
- **Reverse Proxy (Nginx/HAProxy):** Sits in front of the *Server*. When a user visits your website, they do not hit your internal API server (which might be running on a private IP like 10.0.0.5:8080). They hit the public Nginx Reverse Proxy. Nginx then securely forwards the traffic to the internal server.

### Why do we need a Reverse Proxy?

If you have a Node.js Express server, why can't you just expose port 80 to the world?

1. **Security & DDoS Protection:** Nginx acts as a hardened shield. It can drop malformed HTTP requests, rate-limit aggressive IPs, and hide the architecture of your internal network.
2. **Load Balancing:** Nginx can take 100,000 incoming requests and distribute them across 10 different internal Node.js servers using Round Robin or IP Hashing.
3. **SSL/TLS Termination:** Decrypting HTTPS traffic is incredibly CPU intensive. Instead of making your 10 Node.js servers decrypt traffic (wasting compute), you install the SSL certificate on Nginx. Nginx decrypts the traffic at the edge, and forwards fast, unencrypted HTTP traffic to the internal servers via a private, secure subnet.
4. **Static File Serving:** Nginx is phenomenally fast at serving static files from disk (HTML, CSS, Images). It is a waste to use a Node.js interpreter just to serve a static image. Nginx intercepts requests for static assets and serves them directly from disk, completely bypassing the backend API.

<diagram>
graph TD
    Client[Internet Traffic] --> |HTTPS: 443| Nginx[Nginx Reverse Proxy]

    subgraph Inside the Private Network
    Nginx --> |1. Static Assets| Disk[Local Disk /images]
    Nginx --> |2. API Route /api| LB{Load Balancing}
    LB --> |HTTP: 8080| Node1[Node API 1]
    LB --> |HTTP: 8080| Node2[Node API 2]
    LB --> |HTTP: 8080| Node3[Node API 3]
    end
</diagram>

### Nginx vs HAProxy
- **Nginx:** Extremely versatile. It is an excellent web server, reverse proxy, and Layer 7 (HTTP) load balancer. It excels at serving static files and caching.
- **HAProxy (High Availability Proxy):** The gold standard for pure, unadulterated Load Balancing. It is insanely fast (capable of processing millions of TCP connections per second) and offers deep, complex routing metrics. It excels as a Layer 4 (TCP) and Layer 7 load balancer.
  `
    },

    // ── Data Warehousing & Big Data ──────────────────────────────
    {
        id: 'snowflake-redshift',
        category: 'Data Warehousing & Big Data',
        title: 'Snowflake / Redshift',
        summary: 'Cloud-native Data Warehouses configured for massive parallel processing (MPP) for analytical queries over petabytes of historical data.',
        content: `
## Data Warehousing (OLAP vs OLTP)

Standard databases like PostgreSQL or MongoDB are optimized for **OLTP (Online Transaction Processing)**.
- They are designed to do millions of tiny, fast reads and writes (e.g., "Insert this one user's order", or "Update this shopping cart").
- They process data row-by-row.

But what if the CEO asks: *"What was the total revenue generated by users aged 18-25 in California over the last 5 years?"*
- If you run this query on your production PostgreSQL database, it will scan 100 million rows, lock up the CPU, and crash your entire application, causing massive downtime.

You need **OLAP (Online Analytical Processing)**. This is the realm of **Data Warehouses** like Snowflake, Amazon Redshift, and Google BigQuery.

### Columnar Storage Architecture

The secret behind Data Warehouses is how they store data on disk. They do not store it row-by-row. They store it **column-by-column**.

If a table has 50 columns, and you run SELECT SUM(revenue) FROM sales, an OLTP row-database must pull all 50 columns from the disk into memory just to read the one revenue column for each row.
An OLAP columnar database physically stores the entire revenue column together on disk. The disk head reads only that one column in a single lightning-fast sweep, completely ignoring the other 49 columns.

This Columnar Storage combined with **MPP (Massively Parallel Processing)** — where one query is split and run simultaneously across 100 different server nodes — allows analytical queries to process billions of records in seconds.

<diagram>
graph TD
    subgraph OLTP Systems App Databases
    PG[(PostgreSQL Primary)]
    Mongo[(MongoDB)]
    end

    subgraph ETL Pipeline
    Airflow[Apache Airflow] -->|Extracts Data Nightly| PG
    Airflow -->|Extracts Data Nightly| Mongo
    Airflow -->|Transforms to Parquet| Spark[Apache Spark]
    end

    subgraph OLAP Data Warehouse
    Spark -->|Loads| Snowflake[(Snowflake / Redshift)]
    end

    BI[Dashboard / Looker / Tableau] -->|Analytical Queries| Snowflake
</diagram>

### What makes Snowflake Special?

Historically (like with early Redshift), Compute (CPU) and Storage (Disk) were tightly coupled on a server node. If you had 100TB of data but rarely queried it, you still had to buy expensive servers just to hold the disks.

Snowflake revolutionized this by **decoupling compute from storage**:
1. **Centralized Storage:** All data rests in cheap Amazon S3 object storage. You pay almost nothing to keep petabytes of data at rest.
2. **Elastic Compute (Virtual Warehouses):** When an analyst needs to run a massive query, Snowflake spins up a massive "Virtual Warehouse" (a cluster of compute nodes) in seconds, runs the query against the S3 data, returns the result, and immediately shuts the compute cluster down. You only pay for compute by the second.
3. This allows massive concurrency: The Data Science team can run a massive machine learning model using an XL Compute Cluster on the exact same underlying S3 data as the Marketing team using a Small Compute Cluster for basic dashboards, without either team slowing the other down.
  `
    },
    {
        id: 'apache-spark',
        category: 'Data Warehousing & Big Data',
        title: 'Apache Spark',
        summary: 'A unified analytics engine for large-scale data processing with built-in modules for streaming, SQL, ML, and graph processing.',
        content: `
## Apache Spark

When you have Terabytes or Petabytes of raw data sitting in a Data Lake (like S3), you need a compute engine powerful enough to process it. Apache Spark is the de-facto standard for **distributed data processing** at massive scale.

### Why Not Just Use SQL?
If you have 50TB of JSON logs, you cannot load them into a PostgreSQL table. The server would crash. Spark distributes the data across hundreds of worker nodes, processes each chunk in parallel in-memory, and aggregates the results — a technique called **MapReduce on steroids**.

### Core Architecture
1. **Driver Program:** The master process that coordinates the job. It translates your Python/Scala code into a DAG (Directed Acyclic Graph) of execution stages.
2. **Cluster Manager (YARN / Kubernetes):** Allocates physical resources (CPU, RAM) on the cluster.
3. **Executors:** Worker processes that run on individual nodes. Each executor processes a partition of the data in parallel.

### RDDs and DataFrames
- **RDD (Resilient Distributed Dataset):** The original low-level API. An immutable, distributed collection of objects. If a worker crashes, the RDD can be recomputed from the lineage graph.
- **DataFrame API:** The modern, high-level API (like a SQL table). Much faster due to Catalyst query optimizer.

### Interview Use Cases
- **ETL Pipelines:** Extracting raw JSON from S3, cleaning and transforming it, and loading it into Snowflake nightly.
- **Machine Learning at Scale:** Training models on billions of data points using Spark MLlib across 100 worker nodes.
- **Real-Time Stream Processing:** Using Spark Structured Streaming to process Kafka events in micro-batches.

<diagram>
graph TD
    Driver[Spark Driver Program] --> CM[Cluster Manager YARN/K8s]
    CM --> E1[Executor 1 - Partition A]
    CM --> E2[Executor 2 - Partition B]
    CM --> E3[Executor 3 - Partition C]

    S3[(S3 Data Lake)] --> E1
    S3 --> E2
    S3 --> E3

    E1 --> Output[(Result: Snowflake / S3)]
    E2 --> Output
    E3 --> Output
</diagram>
  `
    },

    // ── Container Orchestration ──────────────────────────────────
    {
        id: 'docker-kubernetes',
        category: 'Container Orchestration',
        title: 'Docker & Kubernetes',
        summary: 'Containerization and orchestration platforms for packaging, deploying, and scaling microservices reliably.',
        content: `
## Docker & Kubernetes

### Docker: The Shipping Container for Software
Before Docker, deploying an app meant carefully configuring a server's OS, installing exact library versions, setting environment variables, and praying it works the same in production as on your laptop (it never did — "But it works on my machine!").

**Docker** solves this by packaging your application code, its runtime, libraries, and system tools into a single, portable, lightweight **Container Image**. This image runs identically on any machine — your laptop, a staging server, or a production cluster in AWS. It is a standardized shipping container for software.

### Kubernetes (K8s): The Fleet Manager
Docker handles running a single container. But in production, you have 200 microservices, each needing 5 running replicas. If one container crashes, it must be instantly restarted. If traffic spikes, 10 new containers must be automatically spun up. 

**Kubernetes** is the orchestrator that manages this entire fleet.

### Core K8s Concepts
1. **Pod:** The smallest deployable unit. A Pod wraps one or more Docker containers. It has its own IP address.
2. **Service:** A stable network endpoint that routes traffic to a set of Pods. Even as Pods die and new ones spawn with different IPs, the Service IP remains constant.
3. **Deployment:** A declaration: "I want 5 replicas of my User Service running at all times." K8s ensures this state is maintained.
4. **Horizontal Pod Autoscaler (HPA):** Automatically scales the number of Pods up or down based on CPU/Memory utilization.

### Interview Use Cases
- **Zero-Downtime Deployments:** Rolling updates replace Pods one-by-one without dropping a single user request.
- **Self-Healing Infrastructure:** If a node dies, K8s automatically reschedules its Pods onto healthy nodes.

<diagram>
graph TD
    Dev[Developer] -->|docker push| Registry[(Container Registry)]
    Registry --> K8s[Kubernetes Cluster]

    subgraph K8s Cluster
        Ingress[Ingress / Load Balancer] --> SvcA[Service: User API]
        SvcA --> PodA1[Pod 1]
        SvcA --> PodA2[Pod 2]
        SvcA --> PodA3[Pod 3]

        HPA[Horizontal Pod Autoscaler] -.->|Scale to 5 Pods| SvcA
    end
</diagram>
  `
    },

    // ── Monitoring & Observability ───────────────────────────────
    {
        id: 'prometheus-grafana',
        category: 'Monitoring & Observability',
        title: 'Prometheus & Grafana',
        summary: 'Industry-standard open-source monitoring stack for collecting metrics, alerting, and building visual dashboards.',
        content: `
## Prometheus & Grafana

In a distributed system with 200 microservices, how do you know if a specific service is running slowly? How do you get alerted at 3 AM when your database CPU hits 95%? You need an **Observability Stack**.

### Prometheus: The Metrics Collector
Prometheus is a pull-based monitoring system. Instead of services pushing metrics to a central server, Prometheus **scrapes** (pulls) metrics from each service's /metrics endpoint every 15 seconds.

- **Time-Series Database:** Prometheus stores all scraped metrics as time-stamped data points. You can query "What was the p99 latency of the Checkout Service between 2 PM and 3 PM yesterday?"
- **PromQL:** A powerful query language for slicing and aggregating metric data.
- **AlertManager:** Define rules like "If error_rate > 5% for 5 minutes, send a PagerDuty alert to the on-call engineer."

### Grafana: The Visualization Layer
Grafana connects to Prometheus (and dozens of other data sources like Elasticsearch, CloudWatch) and provides beautiful, real-time dashboards with graphs, gauges, and heatmaps.

### The Three Pillars of Observability
1. **Metrics (Prometheus):** Aggregated numerical counters — request rate, error rate, latency percentiles. "How many requests per second?"
2. **Logs (ELK Stack):** Detailed text output from individual services. "What exact error message did User 123 see?"
3. **Traces (Jaeger / Zipkin):** Following a single request as it hops across 8 different microservices to find the exact bottleneck. "Why did this one request take 4 seconds?"

<diagram>
graph LR
    SvcA[User Service /metrics] --> Prom[(Prometheus)]
    SvcB[Billing Service /metrics] --> Prom
    SvcC[Auth Service /metrics] --> Prom

    Prom --> Grafana[Grafana Dashboards]
    Prom --> Alert[AlertManager]
    Alert --> PD[PagerDuty / Slack]
</diagram>
  `
    }
];
